import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import dashboardServices from "app/services/dashboard-services";
import {DASHBOARD_COLORS, metricChipSx, neutralChipSx} from "./chartColors";

const QUICK_RANGES = [
    {label: "7D", days: 7},
    {label: "30D", days: 30},
    {label: "90D", days: 90}
];

const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const buildQuickRange = (days) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - (days - 1));
    return {
        desde: toDateInputValue(start),
        hasta: toDateInputValue(now)
    };
};

const resolveDefaultGroupBy = (days) => {
    if (days > 60) return "week";
    return "day";
};

const parseLocalDate = (dateString) => new Date(`${dateString}T00:00:00`);
const formatQty = (value) => Number(value || 0).toLocaleString("es-CL", {maximumFractionDigits: 2});
const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleString("es-CL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const formatPeriodLabel = (period, groupBy) => {
    const periodDate = parseLocalDate(period);

    if (groupBy === "month") {
        return periodDate.toLocaleDateString("es-CL", {month: "short", year: "2-digit"});
    }

    if (groupBy === "week") {
        const weekEnd = new Date(periodDate);
        weekEnd.setDate(weekEnd.getDate() + 6);

        return `${periodDate.toLocaleDateString("es-CL", {day: "2-digit", month: "short"})} - ${weekEnd.toLocaleDateString("es-CL", {day: "2-digit", month: "short"})}`;
    }

    return periodDate.toLocaleDateString("es-CL", {day: "2-digit", month: "short"});
};

const OrderPickingTrendTooltip = ({active, payload, label}) => {
    if (!active || !payload?.length) return null;

    const manual = payload.find((item) => item.dataKey === "manual_units")?.value ?? 0;
    const picked = payload.find((item) => item.dataKey === "scanner_units")?.value ?? 0;

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)"
            }}
        >
            <Typography variant="subtitle2" sx={{mb: 0.75}}>{label}</Typography>
            <Typography variant="body2" sx={{color: DASHBOARD_COLORS.manual}}>Manual: {formatQty(manual)}</Typography>
            <Typography variant="body2" sx={{color: DASHBOARD_COLORS.picked}}>Pickeados: {formatQty(picked)}</Typography>
            <Typography variant="body2" sx={{mt: 0.75, pt: 0.75, borderTop: "1px solid", borderColor: "divider", fontWeight: 600}}>Total: {formatQty(manual + picked)}</Typography>
        </Box>
    );
};

const OrderPickingTrend = () => {
    const [quickRange, setQuickRange] = useState(30);
    const [filters, setFilters] = useState(() => {
        const initialRange = buildQuickRange(30);
        return {
            ...initialRange,
            group_by: resolveDefaultGroupBy(30)
        };
    });
    const [series, setSeries] = useState([]);
    const [topPickers, setTopPickers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPicker, setSelectedPicker] = useState(null);
    const [pickerDetails, setPickerDetails] = useState([]);
    const [pickerTotals, setPickerTotals] = useState({manual_units: 0, scanner_units: 0, total_units: 0});
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerError, setPickerError] = useState("");
    const [chartFilter, setChartFilter] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [topPickersByPeriod, setTopPickersByPeriod] = useState([]);
    const [loadingPeriod, setLoadingPeriod] = useState(false);
    const [detailsAllMode, setDetailsAllMode] = useState(false);
    const [detailSearch, setDetailSearch] = useState("");

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            if (!filters.desde || !filters.hasta || filters.desde > filters.hasta) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const response = await dashboardServices.getOrderPickingStats({
                    ...filters,
                    top: 10
                });

                if (!isMounted) return;
                setSeries(response?.series ?? []);
                setTopPickers(response?.top_pickers ?? []);
            } catch (loadError) {
                if (!isMounted) return;
                setSeries([]);
                setTopPickers([]);
                setError("No fue posible cargar la métrica de pickeos.");
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadData();
        return () => {
            isMounted = false;
        };
    }, [filters]);

    useEffect(() => {
        setSelectedPeriod(null);
        setTopPickersByPeriod([]);
    }, [filters]);

    const chartData = useMemo(() => (
        series.map((row) => ({
            ...row,
            label: formatPeriodLabel(row.period, filters.group_by)
        }))
    ), [series, filters.group_by]);

    const totals = useMemo(() => (
        series.reduce((acc, row) => {
            acc.manual += row.manual_units ?? 0;
            acc.scanner += row.scanner_units ?? 0;
            return acc;
        }, {manual: 0, scanner: 0})
    ), [series]);

    const pickerRows = useMemo(() => (
        selectedPeriod ? topPickersByPeriod : topPickers
    ), [selectedPeriod, topPickersByPeriod, topPickers]);

    const pickerRowsTotal = useMemo(() => (
        pickerRows.reduce((acc, row) => {
            acc.manual += row.manual_units ?? 0;
            acc.scanner += row.scanner_units ?? 0;
            acc.total += row.total_units ?? 0;
            return acc;
        }, {manual: 0, scanner: 0, total: 0})
    ), [pickerRows]);

    const filteredPickerDetails = useMemo(() => {
        const term = detailSearch.trim().toLowerCase();
        if (!term) return pickerDetails;
        return pickerDetails.filter((row) => (
            (row.order_code || "").toLowerCase().includes(term) ||
            (row.customer_name || "").toLowerCase().includes(term)
        ));
    }, [pickerDetails, detailSearch]);

    const handleChartClick = async (data) => {
        if (!data?.activePayload?.length) return;
        const clickedPoint = data.activePayload[0]?.payload;
        if (!clickedPoint?.period) return;

        if (selectedPeriod?.period === clickedPoint.period) {
            setSelectedPeriod(null);
            setTopPickersByPeriod([]);
            return;
        }

        let desde = clickedPoint.period;
        let hasta = clickedPoint.period;
        if (filters.group_by === 'week') {
            const end = new Date(`${clickedPoint.period}T00:00:00`);
            end.setDate(end.getDate() + 6);
            hasta = toDateInputValue(end);
        } else if (filters.group_by === 'month') {
            const end = new Date(`${clickedPoint.period}T00:00:00`);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
            hasta = toDateInputValue(end);
        }

        setSelectedPeriod({period: clickedPoint.period, label: clickedPoint.label, desde, hasta});
        setLoadingPeriod(true);
        setTopPickersByPeriod([]);

        try {
            const response = await dashboardServices.getOrderPickingStats({desde, hasta, group_by: filters.group_by, top: 10});
            setTopPickersByPeriod(response?.top_pickers ?? []);
        } catch (_e) {
            setTopPickersByPeriod([]);
        } finally {
            setLoadingPeriod(false);
        }
    };

    const handleQuickRange = (days) => {
        setQuickRange(days);
        const range = buildQuickRange(days);
        setFilters((prev) => ({
            ...prev,
            ...range,
            group_by: resolveDefaultGroupBy(days)
        }));
    };

    const hasInvalidRange = Boolean(filters.desde && filters.hasta && filters.desde > filters.hasta);

    const openPickerModal = async (picker) => {
        const allMode = !picker;
        const detailRange = selectedPeriod
            ? {desde: selectedPeriod.desde, hasta: selectedPeriod.hasta}
            : {desde: filters.desde, hasta: filters.hasta};

        setSelectedPicker(picker || null);
        setDetailsAllMode(allMode);
        setDetailSearch("");
        setModalOpen(true);
        setPickerLoading(true);
        setPickerError("");
        setPickerDetails([]);
        setPickerTotals({manual_units: 0, scanner_units: 0, total_units: 0});

        try {
            const response = await dashboardServices.getPickerPickingDetails({
                user_id: picker?.user_id,
                desde: detailRange.desde,
                hasta: detailRange.hasta
            });
            setPickerDetails(response?.details ?? []);
            setPickerTotals(response?.totals ?? {manual_units: 0, scanner_units: 0, total_units: 0});
        } catch (modalError) {
            setPickerError("No fue posible cargar el detalle del preparador.");
        } finally {
            setPickerLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Card
                sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background: "linear-gradient(180deg, rgba(37,99,235,0.06) 0%, rgba(249,250,251,0.8) 100%)"
                }}
            >
                <CardContent sx={{p: {xs: 2, md: 3}}}>
                    <Stack spacing={2.5}>
                        <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" spacing={2}>
                            <Stack spacing={0.5}>
                                <Typography variant="h4">Productos: Manual vs Pickeado</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Eje Y: cantidad de productos | Eje X: tiempo
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                    sx={metricChipSx(DASHBOARD_COLORS.manual, chartFilter === 'manual')}
                                    label={`Manual: ${formatQty(totals.manual)}`}
                                    onClick={() => setChartFilter(chartFilter === 'manual' ? null : 'manual')}
                                    clickable
                                />
                                <Chip
                                    sx={metricChipSx(DASHBOARD_COLORS.picked, chartFilter === 'scanner')}
                                    label={`Pickeados: ${formatQty(totals.scanner)}`}
                                    onClick={() => setChartFilter(chartFilter === 'scanner' ? null : 'scanner')}
                                    clickable
                                />
                                <Chip
                                    sx={neutralChipSx(chartFilter === null && !selectedPeriod)}
                                    label={`Total: ${formatQty(totals.manual + totals.scanner)}`}
                                    onClick={() => { setChartFilter(null); setSelectedPeriod(null); setTopPickersByPeriod([]); }}
                                    clickable
                                />
                            </Stack>
                        </Stack>

                        <Stack direction={{xs: "column", lg: "row"}} spacing={1.5}>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {QUICK_RANGES.map((option) => (
                                    <Chip
                                        key={option.days}
                                        label={option.label}
                                        clickable
                                        color={quickRange === option.days ? "primary" : "default"}
                                        variant={quickRange === option.days ? "filled" : "outlined"}
                                        onClick={() => handleQuickRange(option.days)}
                                    />
                                ))}
                            </Stack>

                            <TextField
                                size="small"
                                label="Desde"
                                type="date"
                                value={filters.desde}
                                onChange={(event) => setFilters((prev) => ({...prev, desde: event.target.value}))}
                                InputLabelProps={{shrink: true}}
                            />

                            <TextField
                                size="small"
                                label="Hasta"
                                type="date"
                                value={filters.hasta}
                                onChange={(event) => setFilters((prev) => ({...prev, hasta: event.target.value}))}
                                InputLabelProps={{shrink: true}}
                            />

                            <FormControl size="small" sx={{minWidth: 130}}>
                                <InputLabel>Agrupar por</InputLabel>
                                <Select
                                    label="Agrupar por"
                                    value={filters.group_by}
                                    onChange={(event) => setFilters((prev) => ({...prev, group_by: event.target.value}))}
                                >
                                    <MenuItem value="day">Día</MenuItem>
                                    <MenuItem value="week">Semana</MenuItem>
                                    <MenuItem value="month">Mes</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        {hasInvalidRange && (
                            <Alert severity="warning">La fecha inicial no puede ser mayor que la fecha final.</Alert>
                        )}

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                    {loading ? (
                                        <Stack alignItems="center" justifyContent="center" sx={{height: "100%"}}>
                                            <CircularProgress/>
                                        </Stack>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 8}} onClick={handleChartClick} style={{cursor: "pointer"}}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB"/>
                                                <XAxis dataKey="label" tick={{fontSize: 12}} interval="preserveStartEnd"/>
                                                <YAxis
                                                    label={{
                                                        value: "Cantidad de productos",
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        style: {textAnchor: "middle", fill: "#6B7280", fontSize: 12}
                                                    }}
                                                />
                                                <Tooltip content={<OrderPickingTrendTooltip/>}/>
                                                <Legend/>
                                                {selectedPeriod && (
                                                    <ReferenceLine x={selectedPeriod.label} stroke="#0891B2" strokeWidth={2} strokeDasharray="4 4"/>
                                                )}
                                                {(chartFilter === null || chartFilter === 'manual') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="manual_units"
                                                        name="Manual"
                                                        stroke={DASHBOARD_COLORS.manual}
                                                        strokeWidth={3}
                                                        dot={false}
                                                        activeDot={{r: 6}}
                                                    />
                                                )}
                                                {(chartFilter === null || chartFilter === 'scanner') && (
                                                    <Line
                                                        type="monotone"
                                                        dataKey="scanner_units"
                                                        name="Pickeados"
                                                        stroke={DASHBOARD_COLORS.picked}
                                                        strokeWidth={3}
                                                        dot={false}
                                                        activeDot={{r: 6}}
                                                    />
                                                )}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Box sx={{height: 390, bgcolor: "background.paper", borderRadius: 2, p: 1.5}}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1.5}}>
                                        <Typography variant="h6">Top pickeadores</Typography>
                                        {selectedPeriod && (
                                            <Chip
                                                size="small"
                                                label={selectedPeriod.label}
                                                onDelete={() => { setSelectedPeriod(null); setTopPickersByPeriod([]); }}
                                                sx={{bgcolor: "#0891B214", color: "#0891B2", border: "1px solid #0891B266", fontWeight: 600}}
                                            />
                                        )}
                                    </Stack>
                                    <TableContainer sx={{maxHeight: 320}}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Preparador</TableCell>
                                                    <TableCell align="right">Manual</TableCell>
                                                    <TableCell align="right">Pickeados</TableCell>
                                                    <TableCell align="right">Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(loading || loadingPeriod) ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">Cargando...</TableCell>
                                                    </TableRow>
                                                ) : pickerRows.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={4} align="center">
                                                            Sin datos en el rango seleccionado
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    <React.Fragment>
                                                        {pickerRows.map((row) => (
                                                            <TableRow
                                                                key={row.user_id}
                                                                hover
                                                                onClick={() => openPickerModal(row)}
                                                                sx={{cursor: "pointer"}}
                                                            >
                                                                <TableCell>{row.user_name}</TableCell>
                                                                <TableCell align="right">{formatQty(row.manual_units)}</TableCell>
                                                                <TableCell align="right">{formatQty(row.scanner_units)}</TableCell>
                                                                <TableCell align="right">{formatQty(row.total_units)}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow
                                                            hover
                                                            onClick={() => openPickerModal(null)}
                                                            sx={{
                                                                cursor: "pointer",
                                                                "& td": {fontWeight: 700, borderTop: "2px solid", borderColor: "divider"}
                                                            }}
                                                        >
                                                            <TableCell>Total (ver todo)</TableCell>
                                                            <TableCell align="right">{formatQty(pickerRowsTotal.manual)}</TableCell>
                                                            <TableCell align="right">{formatQty(pickerRowsTotal.scanner)}</TableCell>
                                                            <TableCell align="right">{formatQty(pickerRowsTotal.total)}</TableCell>
                                                        </TableRow>
                                                    </React.Fragment>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Grid>
                        </Grid>
                    </Stack>
                </CardContent>
            </Card>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>
                    {detailsAllMode
                        ? "Detalle de pickeos - Todos los preparadores"
                        : (selectedPicker ? `Detalle de pickeos - ${selectedPicker.user_name}` : "Detalle de pickeos")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip sx={metricChipSx(DASHBOARD_COLORS.manual)} label={`Manual: ${formatQty(pickerTotals.manual_units)}`}/>
                            <Chip sx={metricChipSx(DASHBOARD_COLORS.picked)} label={`Pickeados: ${formatQty(pickerTotals.scanner_units)}`}/>
                            <Chip sx={neutralChipSx()} label={`Total: ${formatQty(pickerTotals.total_units)}`}/>
                        </Stack>

                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Buscar por pedido o cliente"
                            value={detailSearch}
                            onChange={(event) => setDetailSearch(event.target.value)}
                        />

                        {pickerError && <Alert severity="error">{pickerError}</Alert>}

                        <TableContainer sx={{maxHeight: 460}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha/Hora</TableCell>
                                        {detailsAllMode && <TableCell>Preparador</TableCell>}
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>Código</TableCell>
                                        <TableCell>Producto</TableCell>
                                        <TableCell align="right">Cantidad</TableCell>
                                        <TableCell>Fuente</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pickerLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={detailsAllMode ? 8 : 7} align="center">Cargando detalle...</TableCell>
                                        </TableRow>
                                    ) : filteredPickerDetails.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={detailsAllMode ? 8 : 7} align="center">
                                                {detailSearch.trim() ? "Sin resultados para la búsqueda" : "Sin movimientos en el rango seleccionado"}
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredPickerDetails.map((row, index) => (
                                        <TableRow key={`${row.order_id}-${row.product_code || "no-code"}-${index}`}>
                                            <TableCell>{formatDateTime(row.picked_at)}</TableCell>
                                            {detailsAllMode && <TableCell>{row.picker_name || "-"}</TableCell>}
                                            <TableCell>{row.order_code || "-"}</TableCell>
                                            <TableCell>{row.customer_name || "-"}</TableCell>
                                            <TableCell>{row.product_code || "-"}</TableCell>
                                            <TableCell>{row.product_name || "-"}</TableCell>
                                            <TableCell align="right">{formatQty(row.picked_qty)}</TableCell>
                                            <TableCell>{row.picked_source_label || "-"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default OrderPickingTrend;
